import './bootstrap';
import '../css/app.css';
import {createApp, h} from 'vue';
import {createInertiaApp} from '@inertiajs/vue3';
import {resolvePageComponent} from 'laravel-vite-plugin/inertia-helpers';
import {ZiggyVue} from '../../vendor/tightenco/ziggy';
import store from './store';
import {defaultConfig as formKitDefaultConfig, plugin as formKitPlugin} from '@formkit/vue';
import {convertOpenAIToFormKit} from '@/composables/useFormKit.js';
import * as Sentry from '@sentry/vue';

const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'Landscape';

// Update intercom settings
function updateWindowIntercomSettings(user) {
    window.intercomSettings = {
        api_base: 'https://api-iam.intercom.io',
        app_id: window.config.intercomWorkspace,
        user_id: user.id,
        name: user.name,
        email: user.email,
        created_at: Math.floor(new Date(user.created_at).getTime() / 1000),
        user_hash: user.intercom_hash,
    };
}

function updateIntercomSettings(user) {
    updateWindowIntercomSettings(user);
    window.Intercom('update', window.intercomSettings);
}

// Intercom initialization
function bootIntercom(user) {
    if (!window.hasOwnProperty('Intercom')) {
        console.log('Intercom widget not loaded... skipping bootIntercom.');
        return;
    }
    if (user) {
        updateWindowIntercomSettings(user);
        window.Intercom('boot', window.intercomSettings);
        updateIntercomSettings(user);
    }
}

createInertiaApp({
    title: (title) => `${title}`,
    resolve: (name) => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
    setup({el, App, props, plugin}) {
        const app = createApp({render: () => h(App, props)})
            .use(plugin)
            .use(ZiggyVue, Ziggy)
            .use(formKitPlugin, formKitDefaultConfig)
            .use(store);

        // Initialize Sentry only in staging and production
        if (['staging', 'production'].includes(window.config.environment)) {
            Sentry.init({
                app,
                dsn: window.config.sentryDsn,
                environment: window.config.environment,
                integrations: [
                    Sentry.browserTracingIntegration(),
                    Sentry.replayIntegration({
                        maskAllText: false,
                        blockAllMedia: false,
                    }),
                ],
                // Performance Monitoring
                tracesSampleRate: window.config.environment === 'production' ? 0.1 : 1.0,
                // Session Replay
                replaysSessionSampleRate: 0.1,
                replaysOnErrorSampleRate: 1.0,

                // Set user context if available
                beforeSend(event, hint) {
                    if (props.initialPage.props.user) {
                        event.user = {
                            id: props.initialPage.props.user.id,
                            email: props.initialPage.props.user.email,
                            username: props.initialPage.props.user.name,
                        };
                    }
                    return event;
                },
            });
        }

        // Initialize Intercom with the current user
        if (props.initialPage.props.user) {
            bootIntercom(props.initialPage.props.user);

            // Also set Sentry user context
            if (['staging', 'production'].includes(window.config.environment)) {
                Sentry.setUser({
                    id: props.initialPage.props.user.id,
                    email: props.initialPage.props.user.email,
                    username: props.initialPage.props.user.name,
                });
            }
        }

        // Watch for user changes
        app.mixin({
            watch: {
                '$page.props.user': {
                    handler(newUser) {
                        if (newUser) {
                            updateIntercomSettings(newUser);

                            // Update Sentry user context
                            if (['staging', 'production'].includes(window.config.environment)) {
                                Sentry.setUser({
                                    id: newUser.id,
                                    email: newUser.email,
                                    username: newUser.name,
                                });
                            }
                        }
                    },
                    deep: true
                }
            }
        });

        return app.mount(el);
    },
    progress: {
        color: '#111',
    },
});

window.form_kit_converter = convertOpenAIToFormKit;
